import { Filter, FilterOperator, getLoanById, LoanStatus, RealTimeResource } from '@nemuru-eng/js-api-client';
import { generatePath, useNavigate } from 'react-router-dom';
import { SWRConfig } from 'swr';

import { useFetchAutoLoanList } from '@/components/modules/AutoLoanList/hooks/useFetchAutoLoanList';
import { AutoLoanDataGridFields } from '@/components/modules/AutoLoanList/utils/AutoLoanDataGridFields/AutoLoanDataGridFields';
import { DetailedLoanStatusTag } from '@/components/shared/components/DetailedLoanStatusTag';
import { CustomGridColDef } from '@/components/shared/components/FetchingDataGrid/FetchingDataGrid';
import { MultiFilteredList } from '@/components/shared/components/MultiFilteredList';
import { Text } from '@/components/shared/components/Text';
import { DYNAMIC_PAGE_PATHS } from '@/components/views/DynamicPageView/config/shared/pathRoutes';
import { ALLOWED_LOAN_STATUSES_FOR_MULTI_SELECT_FILTER } from '@/config/loanConfig';
import { ApplicationType } from '@/config/types';
import { useOperationsContext } from '@/contexts/OperationsContext/OperationsContext';
import { useCache } from '@/hooks/requests/useCache';
import { BooleanFilterDefinition } from '@/hooks/useBooleanFilters';
import { useCollectionScope } from '@/hooks/useCollectionScope';
import { withPermissionsModule } from '@/utils/withPermissionsModule';

const AUTO_BOOLEAN_FILTERS: BooleanFilterDefinition[] = [
  {
    id: 'vin_informed',
    label: <Text tId={'VIN_INFORMED'} />,
    field: 'specs_identification_number',
    operator: FilterOperator.NOT_NULL,
  },
  {
    id: 'delivery_date_informed',
    label: <Text tId={'DELIVERY_DATE_INFORMED'} />,
    field: 'scheduled_delivery_date',
    operator: FilterOperator.NOT_NULL,
  },
  {
    id: 'documentary_incidence',
    label: <Text tId={'WITH_DOCUMENTARY_INCIDENCE'} />,
    field: 'documentation_incidence',
    operator: FilterOperator.EQUAL,
    activeValue: '1',
  },
];

interface AutoLoanListProps {
  listProps?: {
    additionalFilters?: Filter[];
    reducedWidth?: boolean;
  };
}

export const AutoLoanList = ({ listProps }: AutoLoanListProps) => {
  const {
    state: { lastViewedLoanId },
    actions: { updateOperationsContext },
  } = useOperationsContext();

  const scope = useCollectionScope();
  const loan = lastViewedLoanId ? useCache(getLoanById, { loanId: lastViewedLoanId })?.data : undefined;

  const navigate = useNavigate();
  const { fetchAutoLoanList, fetchXLSXExport } = useFetchAutoLoanList({
    additionalFilters: listProps?.additionalFilters,
  });

  return (
    <SWRConfig value={{ revalidateIfStale: false }}>
      <MultiFilteredList
        fetchList={fetchAutoLoanList}
        getXLSXExport={fetchXLSXExport}
        dataGridFields={
          AutoLoanDataGridFields({ reducedWidth: listProps?.reducedWidth }) as unknown as CustomGridColDef[]
        }
        liveUpdates={{ resource: RealTimeResource.AUTO_LOAN_LIST, scope }}
        onRowClick={(selectedRow) => {
          updateOperationsContext({ lastViewedLoanId: selectedRow.id });
          navigate(generatePath(DYNAMIC_PAGE_PATHS.AUTO_LOAN, { loanId: selectedRow.id }));
        }}
        getRowClassName={(params) => {
          if (params.row.id === loan?.id) return 'last-viewed-application';
          else return '';
        }}
        multiselectProps={{
          field: 'status',
          label: <Text tId={'STATUS'} />,
          defaultLabel: <Text tId={'DEFAULT_STATUSES'} />,
          items: ALLOWED_LOAN_STATUSES_FOR_MULTI_SELECT_FILTER,
          checkBoxRender: (value) => <DetailedLoanStatusTag status={value} applicationType={ApplicationType.AUTO} />,
          filtersValueHandler: (valueArray: string[]) => {
            if (valueArray?.includes(LoanStatus.STATUS_ACTIVE)) {
              valueArray.push(LoanStatus.STATUS_ACTIVE_TRIAL);
            }
            if (valueArray?.includes(LoanStatus.STATUS_CANCELLED)) {
              valueArray.push(LoanStatus.STATUS_CANCELLED_TEST);
            }
            return valueArray;
          },
          defaultItems: [
            LoanStatus.STATUS_INITIAL,
            LoanStatus.STATUS_ANALYSING,
            LoanStatus.STATUS_LENDER_HAS_BEEN_SELECTED,
            LoanStatus.STATUS_CONTRACT_SIGNED,
            LoanStatus.STATUS_DOCS_APPROVED,
            LoanStatus.STATUS_ACTIVE,
            LoanStatus.STATUS_DENIED,
            LoanStatus.STATUS_DESISTED,
            LoanStatus.STATUS_CANCELLED,
          ],
        }}
        dateFilterProps={{
          dateField: 'updated_at',
        }}
        booleanFiltersProps={AUTO_BOOLEAN_FILTERS}
        defaultOrderBy={'updated_at'}
        showExport
        disableSelectionOnClick
      />
    </SWRConfig>
  );
};

export const AutoLoanListModule = withPermissionsModule(AutoLoanList);
